/**
 * Resolve OSINT / social platform names to Simple Icons brand SVGs.
 * Icons missing from Simple Icons (trademark removals) fall back to callers' glyph maps.
 */

import * as simpleIconsModule from 'simple-icons';
import type { SimpleIcon } from 'simple-icons';

type SimpleIconsNamespace = Record<string, SimpleIcon | unknown>;

const simpleIcons = simpleIconsModule as unknown as SimpleIconsNamespace;

/** Explicit aliases when our platform key ≠ Simple Icons slug. */
const PLATFORM_TO_SIMPLE_ICON_SLUG: Record<string, string> = {
  gmail: 'gmail',
  google: 'google',
  googlemail: 'gmail',
  googlemaps: 'googlemaps',
  maps: 'googlemaps',
  instagram: 'instagram',
  facebook: 'facebook',
  whatsapp: 'whatsapp',
  telegram: 'telegram',
  x: 'x',
  twitter: 'x',
  youtube: 'youtube',
  github: 'github',
  gitlab: 'gitlab',
  discord: 'discord',
  snapchat: 'snapchat',
  tiktok: 'tiktok',
  pinterest: 'pinterest',
  reddit: 'reddit',
  medium: 'medium',
  quora: 'quora',
  dropbox: 'dropbox',
  spotify: 'spotify',
  apple: 'apple',
  foursquare: 'foursquare',
  hackerrank: 'hackerrank',
  hackerearth: 'hackerearth',
  replit: 'replit',
  fiverr: 'fiverr',
  freelancer: 'freelancer',
  grammarly: 'grammarly',
  goodreads: 'goodreads',
  flickr: 'flickr',
  netflix: 'netflix',
  uber: 'uber',
  ebay: 'ebay',
  paypal: 'paypal',
  chess: 'chessdotcom',
  chessdotcom: 'chessdotcom',
  edx: 'edx',
  wordpress: 'wordpress',
  tumblr: 'tumblr',
  vimeo: 'vimeo',
  soundcloud: 'soundcloud',
  steam: 'steam',
  twitch: 'twitch',
  slack: 'slack',
  zoom: 'zoom',
  skype: 'skype',
  signal: 'signal',
  line: 'line',
  wechat: 'wechat',
  bilibili: 'bilibili',
  dribbble: 'dribbble',
  behance: 'behance',
  stackoverflow: 'stackoverflow',
  npm: 'npm',
  docker: 'docker',
  hubspot: 'hubspot',
  salesforce: 'salesforce',
  shopify: 'shopify',
  stripe: 'stripe',
  visa: 'visa',
  mastercard: 'mastercard',
  airbnb: 'airbnb',
  booking: 'bookingdotcom',
  bookingdotcom: 'bookingdotcom',
};

let slugIndex: Map<string, SimpleIcon> | null = null;
let titleIndex: Map<string, SimpleIcon> | null = null;

function isSimpleIcon(value: unknown): value is SimpleIcon {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as SimpleIcon).slug === 'string' &&
    typeof (value as SimpleIcon).path === 'string' &&
    typeof (value as SimpleIcon).hex === 'string'
  );
}

function ensureIndexes(): void {
  if (slugIndex && titleIndex) return;
  slugIndex = new Map();
  titleIndex = new Map();
  Object.values(simpleIcons).forEach((value) => {
    if (!isSimpleIcon(value)) return;
    slugIndex!.set(value.slug, value);
    titleIndex!.set(normalizeLookupKey(value.title), value);
    titleIndex!.set(normalizeLookupKey(value.slug), value);
  });
}

export function normalizeLookupKey(value: string | null | undefined): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\.com$/i, '')
    .replace(/[^a-z0-9]+/g, '');
}

function toSvgDataUrl(svg: string): string {
  if (typeof btoa !== 'undefined') {
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
  }
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/** Map a free-form platform / label to a Simple Icons slug when possible. */
export function resolveSimpleIconSlug(platform: string | null | undefined): string | null {
  ensureIndexes();
  const raw = String(platform || '').trim();
  if (!raw) return null;

  const normalized = normalizeLookupKey(raw);
  if (!normalized) return null;

  if (PLATFORM_TO_SIMPLE_ICON_SLUG[normalized]) {
    const slug = PLATFORM_TO_SIMPLE_ICON_SLUG[normalized];
    return slugIndex!.has(slug) ? slug : null;
  }

  if (slugIndex!.has(normalized)) return normalized;
  if (titleIndex!.has(normalized)) return titleIndex!.get(normalized)!.slug;

  // Longest alias / slug containment (e.g. "Truecaller India Leak1" won't match SI;
  // "instagram profile" → instagram via alias list + haystack).
  const haystack = raw.toLowerCase();
  const aliasKeys = Object.keys(PLATFORM_TO_SIMPLE_ICON_SLUG).sort((a, b) => b.length - a.length);
  for (const key of aliasKeys) {
    if (haystack.includes(key) || normalized.includes(key)) {
      const slug = PLATFORM_TO_SIMPLE_ICON_SLUG[key];
      if (slugIndex!.has(slug)) return slug;
    }
  }

  // Match against known slugs contained in the platform string.
  // Ignore short fragments ("ros" in "microsoft") — they steal the wrong logo.
  let best: { slug: string; len: number } | null = null;
  for (const slug of slugIndex!.keys()) {
    if (slug.length < 6) continue;
    if (normalized.includes(slug) || haystack.includes(slug)) {
      if (!best || slug.length > best.len) best = { slug, len: slug.length };
    }
  }
  return best?.slug || null;
}

export function resolveSimpleIcon(platform: string | null | undefined): SimpleIcon | null {
  ensureIndexes();
  const slug = resolveSimpleIconSlug(platform);
  if (!slug) return null;
  return slugIndex!.get(slug) || null;
}

const dataUrlCache = new Map<string, string | null>();

export function createSimpleIconDataUrl(
  platform: string | null | undefined,
  options?: { fill?: string; useBrandColor?: boolean },
): string | null {
  const icon = resolveSimpleIcon(platform);
  if (!icon) return null;

  const fill = options?.useBrandColor ? `#${icon.hex}` : options?.fill || '#ffffff';
  const cacheKey = `${icon.slug}|${fill}`;
  if (dataUrlCache.has(cacheKey)) {
    return dataUrlCache.get(cacheKey) || null;
  }

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" role="img">
      <path fill="${fill}" d="${icon.path}"/>
    </svg>
  `.trim();
  const url = toSvgDataUrl(svg);
  dataUrlCache.set(cacheKey, url);
  return url;
}

export function getSimpleIconBrandColor(platform: string | null | undefined): string | null {
  const icon = resolveSimpleIcon(platform);
  return icon ? `#${icon.hex}` : null;
}
